import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUser, faCheckCircle, faInfoCircle, faSpinner, faEdit, faBuilding, faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import toastService from '@frontend/AppSettings/services/ToastService';
import './EditContactPage.css';
import FormInput from '@frontend/Components/Forms/FormInput/FormInput';

interface EditContactPageProps {
  onClose: () => void;
  contact: {
    id: string;
    name: string;
    title: string;
    isPrimary?: boolean;
    contactType: 'client' | 'agent';
    email?: string;
    phone?: string;
    company?: string;
    address?: string;
  };
  stakeholderId: string;
  type: 'individual' | 'organization';
  stakeholderData?: any;
  allContacts?: Array<{
    id: string;
    name: string;
    isPrimary?: boolean;
    contactType: 'client' | 'agent';
  }>;
  onContactUpdated?: (primaryContactId?: string) => void;
}

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  company: string;
  address: string;
  isPrimaryContact: boolean;
}

const EditContactPage: React.FC<EditContactPageProps> = ({ 
  onClose, 
  contact,
  stakeholderId, 
  type,
  stakeholderData,
  allContacts = [],
  onContactUpdated 
}) => {
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    company: '',
    address: '',
    isPrimaryContact: false,
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [fetchingContact, setFetchingContact] = useState<boolean>(true);

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        setFetchingContact(true);
        
        // Parse name into first and last name
        const nameParts = contact.name.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        // Fetch full contact data
        let fullContactData: any = null;
        if (contact.contactType === 'client') {
          fullContactData = await ClientService.getClientById(contact.id);
        } else {
          fullContactData = await AgentsService.getAgent(contact.id);
        }

        if (fullContactData) {
          setFormData({
            firstName: fullContactData.FirstName || firstName,
            lastName: fullContactData.LastName || lastName,
            email: fullContactData.Email || contact.email || '',
            phone: fullContactData.Phone || contact.phone || '',
            title: fullContactData.Title || contact.title || '',
            company: fullContactData.Company || contact.company || stakeholderData?.Company || '',
            address: fullContactData.Address || contact.address || '',
            isPrimaryContact: contact.isPrimary || false,
          });
        } else {
          // Fallback to parsed data from contact prop
          setFormData({
            firstName,
            lastName,
            email: contact.email || '',
            phone: contact.phone || '',
            title: contact.title || '',
            company: contact.company || stakeholderData?.Company || '',
            address: contact.address || '',
            isPrimaryContact: contact.isPrimary || false,
          });
        }
      } catch (error) {
        console.error('Error fetching contact data:', error);
        // Fallback to parsed data
        const nameParts = contact.name.trim().split(' ');
        setFormData({
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          email: contact.email || '',
          phone: contact.phone || '',
          title: contact.title || '',
          company: contact.company || stakeholderData?.Company || '',
          address: contact.address || '',
          isPrimaryContact: contact.isPrimary || false,
        });
      } finally {
        setFetchingContact(false);
      }
    };

    fetchContactData();
  }, [contact, stakeholderData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, isPrimaryContact: e.target.checked });
  };

  const handleClose = () => {
    onClose();
  };

  const handleContactUpdate = async () => {
    setLoading(true);
    setError('');

    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setLoading(false);
      setError('Please fill in all required fields (First Name, Last Name, Email).');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setLoading(false);
      setError('Please enter a valid email address.');
      return;
    }

    try {
      const contactData: any = {
        FirstName: formData.firstName,
        LastName: formData.lastName,
        Email: formData.email,
        Phone: formData.phone || '',
        Company: formData.company || '',
        Title: formData.title || '',
        Address: formData.address || '',
      };

      let success = false;

      // Update the contact
      if (contact.contactType === 'client') {
        success = await ClientService.updateClient(contact.id, contactData);
      } else {
        success = await AgentsService.updateAgent(contact.id, contactData);
      }

      if (success) {
        // Handle primary contact logic
        if (type === 'organization' && formData.isPrimaryContact) {
          // If setting this contact as primary, we need to unset others in the same organization
          // Get all contacts in the organization
          const allClients = await ClientService.getClients();
          const allAgents = await AgentsService.getAgents();
          
          const companyName = formData.company || stakeholderData?.Company;
          if (companyName) {
            // Find all clients and agents with the same company
            const orgClients = allClients.filter((c: any) => c.Company === companyName && c.ClientID !== contact.id);
            const orgAgents = allAgents.filter((a: any) => a.Company === companyName && a.AgentID !== contact.id);
            
            // Note: Since there's no explicit primary contact field in the database,
            // the primary contact is determined by being the first contact in the list.
            // The UI will reflect the primary status based on the order and isPrimary flag.
            // For now, we'll just show a success message and let the parent component handle the refresh.
            toastService.success('Contact updated successfully. Primary contact status will be updated in the organization list.');
          } else {
            toastService.success('Contact updated successfully');
          }
        } else {
          toastService.success('Contact updated successfully');
        }

        // Trigger refresh callback if provided
        // Pass the contact ID if it was set as primary so the parent can reorder the list
        if (onContactUpdated) {
          if (type === 'organization' && formData.isPrimaryContact) {
            onContactUpdated(contact.id);
          } else {
            onContactUpdated();
          }
        }

        // Close modal
        handleClose();
      } else {
        throw new Error('Failed to update contact. Please try again.');
      }
    } catch (error: any) {
      console.error('Error updating contact:', error);
      setError(error.message || 'Failed to update the contact. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingContact) {
    return (
      <div className="edit-contact-overlay" onClick={handleClose}>
        <div className="edit-contact-panel" onClick={(e) => e.stopPropagation()}>
          <div className="edit-contact-loading">
            <FontAwesomeIcon icon={faSpinner} spin />
            <span>Loading contact information...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-contact-overlay" onClick={handleClose}>
      <div className="edit-contact-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="edit-contact-header">
          <div className="edit-contact-header-left">
            <div className="edit-contact-title-row">
              <div className="edit-contact-header-icon">
                <FontAwesomeIcon icon={faEdit} />
              </div>
              <div className="edit-contact-header-text">
                <h2 className="edit-contact-title">Edit Contact</h2>
                <p className="edit-contact-subtitle">Update contact information</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="edit-contact-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="edit-contact-content">
          {/* Basic Information Section */}
          <div className="edit-contact-section edit-contact-section-blue">
            <div className="edit-contact-section-header">
              <div className="edit-contact-section-icon-wrapper edit-contact-icon-blue">
                <FontAwesomeIcon icon={faUser} />
              </div>
              <h3 className="edit-contact-section-title">Basic Information</h3>
            </div>
            <div className="edit-contact-section-content">
              <div className="edit-contact-form-row">
                <FormInput
                  label="First Name*"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="e.g., John"
                  required
                />
                <FormInput
                  label="Last Name*"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="e.g., Smith"
                  required
                />
              </div>
              <FormInput
                label="Email*"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="e.g., john.smith@example.com"
                required
              />
              <FormInput
                label="Phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="e.g., (555) 123-4567"
              />
            </div>
          </div>

          {/* Professional Details Section */}
          <div className="edit-contact-section edit-contact-section-purple">
            <div className="edit-contact-section-header">
              <div className="edit-contact-section-icon-wrapper edit-contact-icon-purple">
                <FontAwesomeIcon icon={faBuilding} />
              </div>
              <h3 className="edit-contact-section-title">Professional Details</h3>
            </div>
            <div className="edit-contact-section-content">
              <FormInput
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Chief Executive Officer, General Counsel"
              />
              <FormInput
                label="Company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="e.g., Acme Corporation"
                disabled={type === 'organization' && !!stakeholderData?.Company}
              />
              {type === 'organization' && stakeholderData?.Company && (
                <div className="edit-contact-info-box">
                  <FontAwesomeIcon icon={faInfoCircle} className="edit-contact-info-icon" />
                  <span>Company is set to match the organization</span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Information Section */}
          <div className="edit-contact-section edit-contact-section-green">
            <div className="edit-contact-section-header">
              <div className="edit-contact-section-icon-wrapper edit-contact-icon-green">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <h3 className="edit-contact-section-title">Additional Information</h3>
            </div>
            <div className="edit-contact-section-content">
              <FormInput
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="e.g., 123 Main St, City, State, ZIP"
              />
            </div>
          </div>

          {/* Primary Contact Section */}
          {type === 'organization' && (
            <div className="edit-contact-section edit-contact-section-white">
              <div className="edit-contact-section-header">
                <h3 className="edit-contact-section-title">Primary Contact</h3>
              </div>
              <div className="edit-contact-section-content">
                <div className="edit-contact-checkbox-wrapper">
                  <label className="edit-contact-checkbox-label">
                    <input
                      type="checkbox"
                      name="isPrimaryContact"
                      checked={formData.isPrimaryContact}
                      onChange={handleCheckboxChange}
                      className="edit-contact-checkbox"
                    />
                    <span className="edit-contact-checkbox-text">
                      Set as primary contact for this organization
                    </span>
                  </label>
                  <div className="edit-contact-info-box">
                    <FontAwesomeIcon icon={faInfoCircle} className="edit-contact-info-icon" />
                    <span>
                      If checked, this contact will be set as the primary contact. The previous primary contact will be automatically unselected.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && <div className="edit-contact-error">{error}</div>}
        </div>

        {/* Footer Actions */}
        <div className="edit-contact-footer">
          <div className="edit-contact-footer-info">
            <FontAwesomeIcon icon={faInfoCircle} className="edit-contact-footer-info-icon" />
            <span>All required fields must be completed</span>
          </div>
          <div className="edit-contact-footer-buttons">
            <button
              type="button"
              className="edit-contact-button edit-contact-button-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="edit-contact-button edit-contact-button-primary"
              onClick={handleContactUpdate}
              disabled={loading}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Updating...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Update Contact
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditContactPage;

